import { logErrorReport } from './firebase';

// --- Configuration ---
const MAX_ERRORS_PER_MINUTE = 5;
const MAX_ERRORS_PER_SESSION = 20;
const MAX_BUFFER_SIZE = 10;
const APP_VERSION = process.env.REACT_APP_VERSION || '2.0.0';
const APP_MODE = process.env.REACT_APP_DEV_MODE === 'true' ? 'dev' : 'prod';

// --- State ---
let currentUid = null;
let errorBuffer = []; // Pre-auth buffer
let seenFingerprints = new Set();
let sessionErrorCount = 0;
let minuteErrorCount = 0;
let minuteResetTimer = null;
let cleanupFn = null;

/**
 * Simple djb2 hash for fingerprinting errors.
 * Produces a short hex string from an input string.
 */
export function djb2Hash(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
  }
  return (hash >>> 0).toString(16);
}

/**
 * Generate a fingerprint from an error message and stack trace.
 * Strips line/column numbers so the same bug across builds produces the same fingerprint.
 */
export function generateFingerprint(message, stack) {
  const normalizedMessage = (message || 'unknown').trim();

  // Take first 3 meaningful stack frames, strip line:col numbers
  const frames = (stack || '')
    .split('\n')
    .filter(line => line.trim().startsWith('at '))
    .slice(0, 3)
    .map(line => line.replace(/:\d+:\d+/g, '').trim())
    .join('|');

  return djb2Hash(normalizedMessage + '|' + frames);
}

/**
 * Check if we're within rate limits.
 * Returns true if the error should be logged, false if rate-limited.
 */
function withinRateLimit() {
  if (sessionErrorCount >= MAX_ERRORS_PER_SESSION) return false;
  if (minuteErrorCount >= MAX_ERRORS_PER_MINUTE) return false;
  return true;
}

/**
 * Increment rate limit counters and set up minute reset.
 */
function incrementCounters() {
  sessionErrorCount++;
  minuteErrorCount++;

  if (!minuteResetTimer) {
    minuteResetTimer = setTimeout(() => {
      minuteErrorCount = 0;
      minuteResetTimer = null;
    }, 60000);
  }
}

/**
 * Extract the session id (and optionally a session owner uid for shared
 * sessions) from a pathname like:
 *   /session/{id}/...
 *   /shared/{ownerUid}/{id}/...
 *   /admin/user/{ownerUid}/session/{id}/...
 * Returns { sessionId, sessionOwnerUid } where either field may be null.
 */
export function extractSessionFromPath(pathname) {
  if (!pathname) return { sessionId: null, sessionOwnerUid: null };
  let m = pathname.match(/^\/session\/([^/]+)/);
  if (m && m[1] !== 'new' && !m[1].startsWith('temp_')) {
    return { sessionId: m[1], sessionOwnerUid: null };
  }
  m = pathname.match(/^\/shared\/([^/]+)\/([^/]+)/);
  if (m) return { sessionId: m[2], sessionOwnerUid: m[1] };
  m = pathname.match(/^\/admin\/user\/([^/]+)\/session\/([^/]+)/);
  if (m) return { sessionId: m[2], sessionOwnerUid: m[1] };
  return { sessionId: null, sessionOwnerUid: null };
}

/**
 * Build the error report object from an error and optional metadata.
 */
function buildErrorReport(error, metadata = {}) {
  const message = error?.message || String(error);
  const stack = error?.stack || '';
  const fingerprint = generateFingerprint(message, stack);

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const { sessionId, sessionOwnerUid } = extractSessionFromPath(pathname);

  return {
    fingerprint,
    message: message.slice(0, 1000), // Cap message length
    stack: stack.slice(0, 4000), // Cap stack length
    componentStack: metadata.componentStack ? metadata.componentStack.slice(0, 2000) : null,
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    appVersion: APP_VERSION,
    mode: APP_MODE,
    timestamp: Date.now(),
    status: 'new',
    source: metadata.source || 'unknown',
    sessionId: sessionId || null,
    sessionOwnerUid: sessionOwnerUid || null,
  };
}

/**
 * Flush buffered errors to Firebase.
 */
async function flushBuffer(uid) {
  const toFlush = errorBuffer.splice(0);
  for (const report of toFlush) {
    try {
      await logErrorReport(uid, report);
    } catch (e) {
      console.error('[ErrorLogger] Failed to flush buffered error:', e);
    }
  }
}

/**
 * Log an error. Called by global handlers and ErrorBoundary.
 * If no UID is set yet, buffers the error for later flushing.
 */
export async function logError(error, metadata = {}) {
  try {
    const report = buildErrorReport(error, metadata);

    // Deduplicate by fingerprint within this session
    if (seenFingerprints.has(report.fingerprint)) return;

    // Check rate limits
    if (!withinRateLimit()) return;

    seenFingerprints.add(report.fingerprint);
    incrementCounters();

    if (currentUid) {
      await logErrorReport(currentUid, report);
    } else {
      // Buffer for later flush when auth resolves
      if (errorBuffer.length < MAX_BUFFER_SIZE) {
        errorBuffer.push(report);
      }
    }
  } catch (e) {
    // Never let the error logger itself throw
    console.error('[ErrorLogger] Failed to log error:', e);
  }
}

/**
 * Set the authenticated user's UID. Flushes any buffered errors.
 * Pass null on sign-out.
 */
export function setErrorLoggerUid(uid) {
  currentUid = uid;
  if (uid && errorBuffer.length > 0) {
    flushBuffer(uid);
  }
}

/**
 * Initialize global error handlers.
 * Call once at app startup (before React renders).
 * Returns a cleanup function to remove handlers.
 */
export function initErrorLogger() {
  // Clean up any previous initialization (e.g., in tests or HMR)
  if (cleanupFn) cleanupFn();

  const handleError = (event) => {
    const error = event.error || new Error(event.message || 'Unknown error');
    logError(error, { source: 'onerror' });
  };

  const handleUnhandledRejection = (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason || 'Unhandled promise rejection'));
    logError(error, { source: 'unhandledrejection' });
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  cleanupFn = () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    if (minuteResetTimer) {
      clearTimeout(minuteResetTimer);
      minuteResetTimer = null;
    }
    cleanupFn = null;
  };

  return cleanupFn;
}

/**
 * Reset internal state. Exposed for testing only.
 */
export function _resetForTesting() {
  currentUid = null;
  errorBuffer = [];
  seenFingerprints = new Set();
  sessionErrorCount = 0;
  minuteErrorCount = 0;
  if (minuteResetTimer) {
    clearTimeout(minuteResetTimer);
    minuteResetTimer = null;
  }
  if (cleanupFn) {
    cleanupFn();
  }
}
